import type { ALGOLIA_OPTIONAL_FILTERS } from "@/modules/jobAds/client/constants"
import type {
  AlgoliaJobAdsRequestParameters,
  FacetsWithValues,
} from "@/modules/jobAds/client/utils/getAlgoliaFacetFiltersFromParams"
import type { OptionalFilters } from "@/modules/jobAds/types/jobAdFromAlgolia"
import type { Location } from "@/modules/locations/locations"

import _omit from "lodash/omit"
import { NIL as NIL_UUID } from "uuid"

import { ALGOLIA_OPTIONAL_FILTERS_V2, LOCALE_NONE, PUBLICATION_TYPES } from "@/modules/jobAds/client/constants"
import { getLocationType } from "@/modules/locations/locations"

const createInitialOptionalFilters = (): Record<keyof OptionalFilters, string[]> => ({
  CONTRACT_TYPE: [],
  LOCALE: [],
  LOCATIONS: [],
  PARTNER_SCHOOL_IDS: [],
  POSITION_CATEGORY_UUID: [],
  PUBLICATION_TYPE_SCORE: [],
  STUDY_LEVELS: [],
})

const computeOptionalFilterFromParam = (
  optionalFilterParams: (typeof ALGOLIA_OPTIONAL_FILTERS)[keyof typeof ALGOLIA_OPTIONAL_FILTERS],
  value: string | number
): string => {
  const { facetName, score } = optionalFilterParams

  return `${facetName}:${value}<score=${score}>`
}

const isOptionalFilterAvailable = (
  searchCriteria: string[] | number[] | Location[] | undefined,
  selectedFilter: string | string[] | null | undefined
): boolean => {
  const hasSearchCriteria = searchCriteria?.length
  const hasNoSelectedFilter = !selectedFilter || selectedFilter?.length === 0

  return Boolean(hasSearchCriteria && hasNoSelectedFilter)
}

export const getOptionalFiltersFromParams = (params: AlgoliaJobAdsRequestParameters): OptionalFilters => {
  const algoliaOptionalFilters = ALGOLIA_OPTIONAL_FILTERS_V2
  const optionalFilters = createInitialOptionalFilters()
  const {
    currentUserSearchCriteria,
    schoolId,
    isLogged,
    contractTypes,
    studyLevels,
    positionCategories,
    locales,
    location,
    userQuery,
    ccPositionCategoryIds,
  } = params

  const hasOptionalStudyLevelFilter = isOptionalFilterAvailable(currentUserSearchCriteria?.studyLevels, studyLevels)
  const hasOptionalContractTypeFilter = isOptionalFilterAvailable(
    currentUserSearchCriteria?.contractTypes,
    contractTypes
  )

  const hasCurrentUserPositionCategoryIds =
    currentUserSearchCriteria?.positionCategoryIds && currentUserSearchCriteria.positionCategoryIds.length > 0
  const currentPositionCategoryIds = hasCurrentUserPositionCategoryIds
    ? currentUserSearchCriteria.positionCategoryIds
    : ccPositionCategoryIds
  const hasOptionalPositionCategoryFilter = isOptionalFilterAvailable(currentPositionCategoryIds, positionCategories)

  const hasOptionalLocaleFilter = isOptionalFilterAvailable(
    currentUserSearchCriteria?.locale,
    locales?.filter(locale => locale !== LOCALE_NONE)
  )
  const hasOptionalLocationFilter = isOptionalFilterAvailable(currentUserSearchCriteria?.locations, location)

  if (hasOptionalStudyLevelFilter) {
    optionalFilters.STUDY_LEVELS =
      currentUserSearchCriteria?.studyLevels.map(studyLevel =>
        computeOptionalFilterFromParam(algoliaOptionalFilters.STUDY_LEVELS, studyLevel)
      ) || []
  }

  if (hasOptionalContractTypeFilter) {
    optionalFilters.CONTRACT_TYPE =
      currentUserSearchCriteria?.contractTypes.map(contractType =>
        computeOptionalFilterFromParam(algoliaOptionalFilters.CONTRACT_TYPE, contractType)
      ) || []
  }

  if (hasOptionalLocaleFilter) {
    optionalFilters.LOCALE =
      currentUserSearchCriteria?.locale.map(locale =>
        computeOptionalFilterFromParam(algoliaOptionalFilters.LOCALE, locale)
      ) || []
  }

  if (hasOptionalLocationFilter) {
    optionalFilters.LOCATIONS =
      currentUserSearchCriteria?.locations.flatMap(userLocation => {
        const locationType = getLocationType(userLocation)
        const locationValue = userLocation[locationType]

        if (!locationType || !locationValue) return []

        const algoliaOptionalLocationFilter = {
          facetName: algoliaOptionalFilters.LOCATIONS.facetName.replace("<location_type>", locationType),
          score: algoliaOptionalFilters.LOCATIONS.score,
        }

        return computeOptionalFilterFromParam(algoliaOptionalLocationFilter, locationValue)
      }) || []
  }

  if (hasOptionalPositionCategoryFilter) {
    optionalFilters.POSITION_CATEGORY_UUID =
      currentPositionCategoryIds?.map(positionCategoryId =>
        computeOptionalFilterFromParam(algoliaOptionalFilters.POSITION_CATEGORY_UUID, positionCategoryId)
      ) || []
  }

  if (!userQuery && currentUserSearchCriteria && schoolId && schoolId !== NIL_UUID) {
    optionalFilters.PARTNER_SCHOOL_IDS = [
      computeOptionalFilterFromParam(algoliaOptionalFilters.PARTNER_SCHOOL_IDS, schoolId),
    ]
  }

  if (!isLogged && !userQuery) {
    optionalFilters.PUBLICATION_TYPE_SCORE = [
      computeOptionalFilterFromParam(
        algoliaOptionalFilters.PUBLICATION_TYPE_SCORE,
        PUBLICATION_TYPES.PREMIUM_SLOT.toString()
      ),
    ]
  }

  return optionalFilters
}

export const formatOptionalFiltersForAlgolia = (
  optionalFilters: OptionalFilters,
  facetFilters: FacetsWithValues
): string[] => {
  const hasLocaleFacetFilter = facetFilters?.LOCALE && facetFilters.LOCALE.length > 0
  const availableOptionalFilters = _omit(optionalFilters, hasLocaleFacetFilter ? ["LOCALE"] : [])

  return Object.values(availableOptionalFilters)
    .filter(facet => facet.length > 0)
    .flat()
}

export const computeAlgoliaOptionalFilters = (
  params: AlgoliaJobAdsRequestParameters,
  facetFilters: FacetsWithValues
): string[] => formatOptionalFiltersForAlgolia(getOptionalFiltersFromParams(params), facetFilters)
